<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <!-- Reset Password v1 -->
      <b-card class="mb-0">

        <!-- logo -->
        <b-link class="brand-logo">
          <vuexy-logo />

          <h2 class="brand-text text-primary ml-1">
            Vuexy
          </h2>
        </b-link>

        <b-card-title class="mb-1">
          Reset Password 🔒
        </b-card-title>
        <b-card-text class="mb-2">
          Your new password must be different from previously used passwords
        </b-card-text>

        <!-- form -->
        <validation-observer ref="simpleRules">
          <b-form
            method="POST"
            class="auth-reset-password-form mt-2"
            @submit.prevent="validationForm"
          >

            <!-- password -->
            <b-form-group
              label="New Password"
              label-for="reset-password-new"
            >
              <validation-provider
                #default="{ errors }"
                name="Password"
                vid="Password"
                rules="required|password"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid':null"
                >
                  <b-form-input
                    id="reset-password-new"
                    v-model="password"
                    :type="password1FieldType"
                    :state="errors.length > 0 ? false:null"
                    class="form-control-merge"
                    name="reset-password-new"
                    placeholder="············"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="password1ToggleIcon"
                      @click="togglePassword1Visibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-danger" v-if="serverErrors && serverErrors.password">{{ serverErrors.password[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- confirm password -->
            <b-form-group
              label-for="reset-password-confirm"
              label="Confirm Password"
            >
              <validation-provider
                #default="{ errors }"
                name="Confirm Password"
                rules="required|confirmed:Password"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid':null"
                >
                  <b-form-input
                    id="reset-password-confirm"
                    v-model="password_confirmation"
                    :type="password2FieldType"
                    class="form-control-merge"
                    :state="errors.length > 0 ? false:null"
                    name="reset-password-confirm"
                    placeholder="············"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="password2ToggleIcon"
                      @click="togglePassword2Visibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-danger" v-if="serverErrors && serverErrors.password_confirmation">{{ serverErrors.password_confirmation[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- submit button -->
            <b-button
              block
              type="submit"
              variant="primary"
            >
              Set New Password
            </b-button>
          </b-form>
        </validation-observer>

        <p class="text-center mt-2">
          <b-link :to="{name:'auth-login-v1'}">
            <feather-icon icon="ChevronLeftIcon" /> Back to login
          </b-link>
        </p>

      </b-card>
    <!-- /Reset Password v1 -->
    </div>
  </div>

</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import {
  BCard, BCardTitle, BCardText, BForm, BFormGroup, BInputGroup, BInputGroupAppend, BLink, BFormInput, BButton,
} from 'bootstrap-vue'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onUnmounted, onMounted, computed } from '@vue/composition-api'
import store from '@/store'
import authenticationStoreModule from '@/views/pages/authentication/authenticationStoreModule'
import router from '@/router'

export default {
  components: {
    VuexyLogo,
    BCard,
    BButton,
    BCardTitle,
    BCardText,
    BForm,
    BFormGroup,
    BInputGroup,
    BLink,
    BFormInput,
    BInputGroupAppend,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {},
  props: {},

  setup(props, context){
    const serverErrors = ref(null)
    const password = ref(null)
    const password_confirmation = ref(null)

    // Toggle Password
    const password1FieldType = ref('password')
    const password2FieldType = ref('password')

    const CROMIS_STORE_MODULE_NAME = 'cromis-authentication'

    // Register module
    if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) store.registerModule(CROMIS_STORE_MODULE_NAME, authenticationStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
        if (store.hasModule(CROMIS_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_STORE_MODULE_NAME)
    })

    const password1ToggleIcon = computed(() => (password1FieldType.value === 'password' ? 'EyeIcon' : 'EyeOffIcon'))

    const password2ToggleIcon = computed(() => (password2FieldType.value === 'password' ? 'EyeIcon' : 'EyeOffIcon'))

    const togglePassword1Visibility = () => {
      password1FieldType.value = password1FieldType.value === 'password' ? 'text' : 'password'
    }

    const togglePassword2Visibility = () => {
      password2FieldType.value = password2FieldType.value === 'password' ? 'text' : 'password'
    }

    const validationForm = async () => {
      serverErrors.value = null
      await store.dispatch('cromis-authentication/changePassword', { password: password.value, password_confirmation: password_confirmation.value })
              .then(response => {
                  if(response.status === 200){
                    localStorage.clear()
                    router.push({ name: 'auth-login-v1' })
                  }
              })
              .catch(error => {
                if(error.response.status === 422){
                    serverErrors.value = error.response.data.errors
                }
                else{
                  console.log(error)
                    context.root.$swal({
                        icon: 'error',
                        title: 'Server Error',
                        text: 'Something went wrong. See tech support',
                        timer: 3000,
                        showConfirmButton: true,
                        customClass: {
                            confirmButton: 'btn btn-danger',
                        },
                        buttonsStyling: false,
                    })
                }
              })
    }

    return {
      serverErrors,
      password,
      password_confirmation,
      required,
      password1FieldType,
      password2FieldType,
      password1ToggleIcon,
      password2ToggleIcon,
      togglePassword1Visibility,
      togglePassword2Visibility,
      validationForm,
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
